/**
 * Workspace Governed Chat V1
 *
 * Intent routing and explanation interface for the workspace.
 * Chat explains and routes; it does not execute, approve, publish, or create authority.
 *
 * Features:
 * - Response detail levels: Quick, Explain, Evidence
 * - Personas: Simple, Builder, Reviewer, Auditor
 * - Intent routing to workspace surfaces
 * - Read-only: no execution, approval, publishing, mutation
 * - No internal leakage: no paths, module names, hashes, stack traces
 *
 * Intent Routing:
 * - build/start → Best Next Move / Agent Proposal Flow
 * - explain recommendation → Best Next Move explanation
 * - show proof/evidence → continuity / verify surface
 * - prepare public communication → /signals
 * - ask about project/work/memory → workspace tabs
 * - execute/run/deploy → proposal review (not execution)
 *
 * INVARIANT:
 * Governed chat is an interface and router only. It explains and routes; it does not execute, approve, publish, or create authority.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, ChevronDown, ChevronUp, Zap, BookOpen, FileText, User, Settings, Sparkles } from 'lucide-react';

type DetailLevel = 'quick' | 'explain' | 'evidence';
type Persona = 'simple' | 'builder' | 'reviewer' | 'auditor';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  detailLevel?: DetailLevel;
  persona?: Persona;
  routedTo?: string;
}

interface IntentRoute {
  intent: string;
  surface: string;
  description: string;
}

const INTENT_ROUTES: IntentRoute[] = [
  { intent: 'build', surface: 'Best Next Move', description: 'Routing to Best Next Move for action suggestions' },
  { intent: 'start', surface: 'Agent Proposal Flow', description: 'Opening Agent Proposal Flow to review and execute' },
  { intent: 'explain', surface: 'Best Next Move Explanation', description: 'Explaining why this move was selected' },
  { intent: 'proof', surface: 'Continuity Verification', description: 'Showing continuity verification status' },
  { intent: 'evidence', surface: 'Verify Surface', description: 'Showing verified evidence summary' },
  { intent: 'signal', surface: '/signals', description: 'Routing to public signal flow' },
  { intent: 'project', surface: 'Workspace Tabs', description: 'Navigating to project information' },
  { intent: 'work', surface: 'Work Tab', description: 'Navigating to your verified contributions' },
  { intent: 'memory', surface: 'Memory Tab', description: 'Navigating to your portable artifacts' },
  { intent: 'execute', surface: 'Agent Proposal Flow', description: 'Routing to proposal review for execution approval' },
  { intent: 'run', surface: 'Agent Proposal Flow', description: 'Routing to proposal review for execution approval' },
  { intent: 'deploy', surface: 'Agent Proposal Flow', description: 'Routing to proposal review for deployment approval' },
];

const PERSONA_DESCRIPTIONS: Record<Persona, string> = {
  simple: 'Simple: Clear, concise explanations for general understanding',
  builder: 'Builder: Technical details for implementation context',
  reviewer: 'Reviewer: Critical analysis and verification focus',
  auditor: 'Auditor: Compliance and governance verification focus'
};

const DETAIL_LEVEL_DESCRIPTIONS: Record<DetailLevel, string> = {
  quick: 'Quick: Brief summary answer',
  explain: 'Explain: Detailed explanation with context',
  evidence: 'Evidence: Full response with supporting evidence'
};

interface GovernedChatProps {
  onRouteToSurface?: (surface: string) => void;
  onOpenProposalFlow?: () => void;
}

export const GovernedChat: React.FC<GovernedChatProps> = ({ onRouteToSurface, onOpenProposalFlow }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      role: 'assistant',
      text: 'Hello! I\'m your governed workspace assistant. I can help explain recommendations, route you to different surfaces, and provide context about your work. How can I help you today?',
      detailLevel: 'quick',
      persona: 'simple'
    }
  ]);
  const [input, setInput] = useState('');
  const [detailLevel, setDetailLevel] = useState<DetailLevel>('quick');
  const [persona, setPersona] = useState<Persona>('builder');
  const [expandedMessages, setExpandedMessages] = useState<Record<string, boolean>>({});

  const detectIntent = (text: string): IntentRoute | null => {
    const lowerText = text.toLowerCase();
    for (const route of INTENT_ROUTES) {
      if (lowerText.includes(route.intent)) {
        return route;
      }
    }
    return null;
  };

  const generateResponse = (userText: string, intentRoute: IntentRoute | null): string => {
    
    // Persona-based tone adjustment
    let tone = '';
    switch (persona) {
      case 'simple':
        tone = 'Here\'s a straightforward answer: ';
        break;
      case 'builder':
        tone = 'From a technical perspective: ';
        break;
      case 'reviewer':
        tone = 'Let me analyze this critically: ';
        break;
      case 'auditor':
        tone = 'From a compliance standpoint: ';
        break;
    }

    // Detail level adjustment
    let detail = '';
    switch (detailLevel) {
      case 'quick':
        detail = 'Briefly, ';
        break;
      case 'explain':
        detail = 'In detail, ';
        break;
      case 'evidence':
        detail = 'With full evidence: ';
        break;
    }

    // Intent-based responses
    if (intentRoute) {
      if (intentRoute.intent === 'build' || intentRoute.intent === 'start') {
        return `${tone}${detail}I can help you get started. I'm routing you to the Best Next Move panel where you can see the recommended action and alternatives. From there, you can open the Agent Proposal Flow to review and execute the change.`;
      }
      if (intentRoute.intent === 'explain') {
        return `${tone}${detail}The Best Next Move engine selected this action based on dependency satisfaction, risk level, and unblock potential. The primary recommendation has the highest score across these weighted criteria. You can see the detailed reasoning in the "Why this move?" section above.`;
      }
      if (intentRoute.intent === 'proof' || intentRoute.intent === 'evidence') {
        return `${tone}${detail}I can show you the verification status. The continuity verification process checks that all artifacts are valid continuations of prior governed artifacts. This ensures the integrity of your work history. I'm routing you to the verification surface.`;
      }
      if (intentRoute.intent === 'signal') {
        return `${tone}${detail}I can help you prepare public-facing signals. This is for sharing verified work with external audiences while maintaining governance boundaries. I'm routing you to the signals surface.`;
      }
      if (intentRoute.intent === 'project' || intentRoute.intent === 'work' || intentRoute.intent === 'memory') {
        return `${tone}${detail}I can help you navigate your workspace. You can access your project information, verified contributions, and portable memory artifacts through the tabs above. Which would you like to explore?`;
      }
      if (intentRoute.intent === 'execute' || intentRoute.intent === 'run' || intentRoute.intent === 'deploy') {
        return `${tone}${detail}For safety and governance compliance, I cannot execute directly. I'm routing you to the Agent Proposal Flow where you can review the proposed changes before approval. This ensures all actions go through proper verification and receipt generation.`;
      }
    }

    // Default responses based on persona and detail level
    if (detailLevel === 'quick') {
      return `${tone}I'm here to help with your workspace. You can ask me about recommendations, verification, or how to navigate different surfaces.`;
    }
    if (detailLevel === 'explain') {
      return `${tone}I'm your governed workspace assistant. I can explain recommendations, route you to different surfaces (Best Next Move, Agent Proposal Flow, verification, signals), and provide context about your work and memory. All actions require proper governance approval.`;
    }
    if (detailLevel === 'evidence') {
      return `${tone}I'm your governed workspace assistant. I can explain recommendations, route you to different surfaces (Best Next Move, Agent Proposal Flow, verification, signals), and provide context about your work and memory. All actions require proper governance approval through the Agent Proposal Flow with MoveSelectionReceipt verification.`;
    }

    return `${tone}${detail}I'm here to help with your workspace. What would you like to know?`;
  };

  const handleSendMessage = () => {
    if (!input.trim()) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      text: input
    };

    const intentRoute = detectIntent(input);
    const responseText = generateResponse(input, intentRoute);

    const assistantMessage: ChatMessage = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      text: responseText,
      detailLevel,
      persona,
      routedTo: intentRoute?.surface
    };

    setMessages(prev => [...prev, userMessage, assistantMessage]);
    setInput('');

    // Route to surface if intent detected
    if (intentRoute && onRouteToSurface) {
      if (intentRoute.surface === 'Agent Proposal Flow' && onOpenProposalFlow) {
        onOpenProposalFlow();
      } else if (intentRoute.surface === 'Best Next Move') {
        // Best Next Move is already visible, just scroll to it
        onRouteToSurface('best-next-move');
      } else if (intentRoute.surface === '/signals') {
        onRouteToSurface('/signals');
      } else if (intentRoute.surface === 'Work Tab') {
        onRouteToSurface('work-tab');
      } else if (intentRoute.surface === 'Memory Tab') {
        onRouteToSurface('memory-tab');
      }
    }
  };

  const toggleExpand = (messageId: string) => {
    setExpandedMessages(prev => ({ ...prev, [messageId]: !prev[messageId] }));
  };

  return (
    <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
      {/* Header with controls */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <MessageSquare className="w-5 h-5 text-purple-400" />
          <h3 className="text-lg font-semibold text-white">Workspace Assistant</h3>
        </div>
        <div className="flex items-center space-x-2 text-xs">
          <span className="text-gray-400">Detail:</span>
          <select
            value={detailLevel}
            onChange={(e) => setDetailLevel(e.target.value as DetailLevel)}
            className="bg-gray-700 text-white px-2 py-1 rounded border border-gray-600"
          >
            <option value="quick">Quick</option>
            <option value="explain">Explain</option>
            <option value="evidence">Evidence</option>
          </select>
          <span className="text-gray-400 ml-2">Persona:</span>
          <select
            value={persona}
            onChange={(e) => setPersona(e.target.value as Persona)}
            className="bg-gray-700 text-white px-2 py-1 rounded border border-gray-600"
          >
            <option value="simple">Simple</option>
            <option value="builder">Builder</option>
            <option value="reviewer">Reviewer</option>
            <option value="auditor">Auditor</option>
          </select>
        </div>
      </div>

      {/* Chat messages */}
      <div className="space-y-4 mb-4 max-h-96 overflow-y-auto">
        {messages.map((msg) => {
          const isUser = msg.role === 'user';
          return (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-lg p-3 ${
                  isUser
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-700 text-gray-100'
                }`}
              >
                <p className="text-sm mb-2">{msg.text}</p>
                {!isUser && msg.routedTo && (
                  <div className="flex items-center space-x-1 text-xs text-purple-300">
                    <Sparkles className="w-3 h-3" />
                    <span>Routed to: {msg.routedTo}</span>
                  </div>
                )}
                {!isUser && (
                  <button
                    onClick={() => toggleExpand(msg.id)}
                    className="flex items-center space-x-1 text-xs text-gray-400 hover:text-gray-300 mt-2"
                  >
                    {expandedMessages[msg.id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    <span>{expandedMessages[msg.id] ? 'Hide' : 'Show'} details</span>
                  </button>
                )}
                {!isUser && expandedMessages[msg.id] && (
                  <div className="mt-2 pt-2 border-t border-gray-600 text-xs text-gray-400 space-y-1">
                    <div className="flex items-center space-x-1">
                      <Zap className="w-3 h-3" />
                      <span>{DETAIL_LEVEL_DESCRIPTIONS[msg.detailLevel || 'quick']}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <User className="w-3 h-3" />
                      <span>{PERSONA_DESCRIPTIONS[msg.persona || 'simple']}</span>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Input */}
      <div className="flex space-x-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
          placeholder="Ask about recommendations, verification, or navigation..."
          className="flex-1 bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 placeholder-gray-400 focus:border-purple-500 focus:outline-none"
        />
        <button
          onClick={handleSendMessage}
          disabled={!input.trim()}
          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Send
        </button>
      </div>

      {/* Read-only notice */}
      <div className="mt-3 text-xs text-gray-500">
        <span className="flex items-center space-x-1">
          <Settings className="w-3 h-3" />
          <span>Read-only assistant: explains and routes, does not execute or approve</span>
        </span>
      </div>
    </div>
  );
};
